/**
 * Vorlage „Sieben“.
 *
 * Ein feines Sieb wird über der Schüssel leicht hin und her bewegt. Mehl
 * rieselt hindurch und bildet einen lockeren Berg ohne Klümpchen.
 */
import { ease, segment, wave } from '../geometry.ts';
import { path } from '../paths.ts';
import { compact, faded, group, shape, styles } from '../scene.ts';
import { falling } from '../parts/effects.ts';
import { flourSieve } from '../parts/kitchen.ts';
import { counter, doubleArrow } from '../parts/stage.ts';
import { bowl, interiorClip } from '../parts/vessels.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

const BOWL = { cx: 120, rim: 88, width: 136, height: 46 } as const;
const SIEVE = { cx: 124, rim: 34, width: 76 } as const;

/** Zeitplan. */
const TIME = { pile: [0.04, 0.84], flowIn: [0.02, 0.08], flowOut: [0.8, 0.86], reset: [0.9, 1] } as const;

/** Vorlage „Sieben“. */
export const mixSiftTemplate = defineTemplate({
  id: 'mix.sift',
  group: 'mix',
  title: 'Sieben',
  description:
    'Mehl oder Puderzucker wird durch ein feines Sieb gegeben. Leichtes Klopfen lässt es gleichmäßig und ohne Klümpchen hindurchrieseln.',
  durationSeconds: 5,
  keywords: ['sieben', 'durchsieben', 'mehl sieben', 'darüber sieben', 'bestäuben', 'puderzucker', 'sieb'],
  stillTime: 0.5,
  parameters: [choice('item', 'Zutat', { flour: 'Mehl', sugar: 'Puderzucker', cocoa: 'Kakao' }, 'flour')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (lower.includes('kakao')) return { item: 'cocoa' };
    if (lower.includes('puderzucker')) return { item: 'sugar' };
    if (lower.includes('mehl')) return { item: 'flour' };
    return {};
  },
  scene(values, t) {
    const cocoa = textValue(values, 'item', 'flour') === 'cocoa';
    const vessel = bowl(BOWL.cx, BOWL.rim, BOWL.width, BOWL.height);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const pile = ease.outQuad(segment(t, ...TIME.pile));
    const flow =
      ease.inOutSine(segment(t, ...TIME.flowIn)) * (1 - ease.inOutSine(segment(t, ...TIME.flowOut)));
    const shake = wave(t, 8) * 5 * flow;
    const moundTop = BOWL.rim + 14 - pile * 22;
    const mound = path()
      .move(BOWL.cx - 50, BOWL.rim + 22)
      .curve(BOWL.cx - 26, moundTop, BOWL.cx + 22, moundTop, BOWL.cx + 50, BOWL.rim + 22)
      .close()
      .toString();
    const moundStyle = cocoa ? ({ fill: 'deep', stroke: 'ink', strokeWidth: 2 } as const) : styles.snow();
    return compact([
      ...counter(),
      ...vessel.back,
      pile > 0.01
        ? faded(1 - reset, [group([shape(mound, moundStyle)], { clip: interiorClip(vessel, 30) })])
        : null,
      ...vessel.front,
      faded(flow, [
        ...falling(SIEVE.cx + shake, SIEVE.rim + 18, moundTop + 4, 46, 22, t, {
          seed: 12,
          size: 1.3,
          loops: 4,
          tone: cocoa ? 'deep' : 'shade',
        }),
      ]),
      group(flourSieve(SIEVE.cx + shake, SIEVE.rim, SIEVE.width)),
    ]);
  },
  hints() {
    return [doubleArrow({ x: SIEVE.cx - 30, y: SIEVE.rim - 16 }, { x: SIEVE.cx + 30, y: SIEVE.rim - 16 })];
  },
});
